// src/components/DashboardView.tsx
import React, { useState } from 'react';
import { Activity, Euro, ArrowUpDown, AlertTriangle, User, Link, CreditCard, Loader2, Repeat, Pencil, Printer, Trash2 } from 'lucide-react';
import { formatPrice, formatDateEU } from '../utils/formatters';

interface DashboardViewProps {
    jobs: any[];
    customers: any[];
    searchQuery: string;
    
    // Functies
    setActiveTab: (tab: string) => void;
    getCustomerName: (id: string) => string;
    getRacketName: (job: any) => string;
    openCustomerHistory: (customer: any) => void;
    cycleStatus: (job: any) => void;
    togglePaid: (job: any) => void;
    duplicateJob: (job: any) => void;
    openJobEditModal: (job: any) => void;
    openPrintModal: (job: any) => void;
    handleBulkDeleteJobs: () => void;
    
    // Selectie logica voor printen (Toegevoegd)
    selectedJobIds?: Set<string>; 
    toggleJobSelection?: (id: string) => void;
    handleSelectAllJobs?: (filteredJobs: any[]) => void;
    handleBulkPrintJobs?: () => void;
    
    // Mollie gerelateerd
    generateMollieLink: (job: any) => void;
    setPaymentModalJob: (job: any) => void;
    generatingMollieId: string | null;
}

export default function DashboardView({
    jobs, customers, searchQuery,
    setActiveTab, getCustomerName, getRacketName, openCustomerHistory,
    cycleStatus, togglePaid, duplicateJob, openJobEditModal, openPrintModal, handleBulkDeleteJobs,
    selectedJobIds = new Set(), toggleJobSelection = () => {}, handleSelectAllJobs = () => {}, handleBulkPrintJobs = () => {},
    generateMollieLink, setPaymentModalJob, generatingMollieId
}: DashboardViewProps) {
    
    // LOKALE STATE
    const [dashboardSortConfig, setDashboardSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    // LOKALE FUNCTIES
    const requestDashboardSort = (key: string) => {
        let direction = 'ascending';
        if (dashboardSortConfig.key === key && dashboardSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setDashboardSortConfig({ key, direction });
    };

    // BEREKENINGEN
    const today = new Date().toISOString().split('T')[0];
    const currentYear = new Date().getFullYear();
    const shortYear = currentYear.toString().slice(-2);
    
    const totalRevenueYTD = jobs.reduce((sum, job) => {
        if (job.date && job.date.length === 4) return sum; 
        const jobDate = job.completedAt ? new Date(job.completedAt) : new Date(job.date);
        if (!isNaN(jobDate.getTime()) && jobDate.getFullYear() === currentYear) {
            return sum + (job.price || 0);
        }
        return sum;
    }, 0);

    const totalJobsYTD = jobs.reduce((count, job) => {
        if (job.date && job.date.length === 4) return count;
        const jobDate = job.completedAt ? new Date(job.completedAt) : new Date(job.date);
        if (!isNaN(jobDate.getTime()) && jobDate.getFullYear() === currentYear) {
            return count + 1;
        }
        return count;
    }, 0);

    let filteredJobs = jobs.filter(job => {
        if (!getCustomerName(job.customerId).toLowerCase().includes(searchQuery.toLowerCase())) return false;
        const isDone = job.status === 'Klaar';
        const isPaid = job.paid === true;
        if (!isDone || !isPaid) return true;
        return (job.completedAt ? new Date(job.completedAt).toISOString().split('T')[0] : job.date) === today;
    });

    filteredJobs.sort((a, b) => {
        let valA = a[dashboardSortConfig.key];
        let valB = b[dashboardSortConfig.key];

        if (dashboardSortConfig.key === 'price') {
            valA = parseFloat(valA) || 0;
            valB = parseFloat(valB) || 0;
        } else if (dashboardSortConfig.key === 'createdAt') {
            valA = parseInt(valA) || 0;
            valB = parseInt(valB) || 0;
        } else if (dashboardSortConfig.key === 'deadline') {
            valA = a.deadline ? new Date(`${a.deadline} ${a.deadlineTime || '00:00'}`).getTime() : Infinity;
            valB = b.deadline ? new Date(`${b.deadline} ${b.deadlineTime || '00:00'}`).getTime() : Infinity;
        } else {
            valA = (valA || '').toString().toLowerCase();
            valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return dashboardSortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return dashboardSortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });

    const allSelected = filteredJobs.length > 0 && filteredJobs.every(j => selectedJobIds.has(j.id));

    return (
        <div className="space-y-4 md:space-y-6 animate-in fade-in duration-500">
            {/* COMPACTE STATS OP MOBIEL */}
            <div className="grid grid-cols-2 gap-2 md:gap-6">
                <div className="bg-white dark:bg-slate-800 p-3 md:p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center text-left">
                    <div className="p-2 md:p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl mr-2 md:mr-4 shrink-0">
                        <Activity className="w-5 h-5 md:w-6 md:h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] md:text-sm text-slate-500 dark:text-slate-400 truncate leading-tight">Bespannen</p>
                        <p className="text-base md:text-2xl font-bold text-slate-800 dark:text-white truncate leading-tight">{totalJobsYTD}</p>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-800 p-3 md:p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center text-left">
                    <div className="p-2 md:p-3 bg-green-50 dark:bg-green-900/30 rounded-xl mr-2 md:mr-4 shrink-0">
                        <Euro className="w-5 h-5 md:w-6 md:h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] md:text-sm text-slate-500 dark:text-slate-400 truncate leading-tight">Omzet '{shortYear}</p>
                        <p className="text-base md:text-2xl font-bold text-slate-800 dark:text-white truncate leading-tight">€{formatPrice(totalRevenueYTD)}</p>
                    </div>
                </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center flex-wrap gap-2">
                    <div className="flex items-center gap-4">
                        <h3 className="font-bold text-slate-800 dark:text-white">Recente Opdrachten</h3>
                        {selectedJobIds.size > 0 && (
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={handleBulkPrintJobs}
                                    className="flex items-center px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition text-sm font-bold"
                                >
                                    <Printer className="w-4 h-4 mr-2" />
                                    Print ({selectedJobIds.size})
                                </button>
                                <button 
                                    onClick={handleBulkDeleteJobs}
                                    className="flex items-center px-3 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition text-sm font-bold"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Verwijder ({selectedJobIds.size})
                                </button>
                            </div>
                        )}
                    </div>
                    <button onClick={()=>setActiveTab('new-job')} className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:underline shrink-0">+ Nieuwe toevoegen</button>
                </div>
                
                <div className="overflow-x-auto w-full">
                    {filteredJobs.length > 0 ? (
                        <>
                        {/* DESKTOP WEERGAVE */}
                        <table className="hidden md:table w-full text-sm text-left text-slate-600 dark:text-slate-300">
                            <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900/50">
                                <tr>
                                    <th className="px-4 py-3 w-10">
                                        <input 
                                            type="checkbox" 
                                            checked={allSelected} 
                                            onChange={() => handleSelectAllJobs(filteredJobs)}
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                                        />
                                    </th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestDashboardSort('createdAt')}>Datum/Klant <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3">Data</th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestDashboardSort('deadline')}>Deadline <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3 cursor-pointer hover:text-indigo-600 transition" onClick={() => requestDashboardSort('status')}>Status <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                    <th className="px-6 py-3 text-right cursor-pointer hover:text-indigo-600 transition" onClick={() => requestDashboardSort('price')}>Kosten <ArrowUpDown className="w-3 h-3 inline ml-1"/></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {filteredJobs.map(job => {
                                    const deadlineDate = job.deadline ? new Date(`${job.deadline}T${job.deadlineTime || '23:59'}`) : null;
                                    const todayDate = new Date();
                                    const isUrgent = deadlineDate && deadlineDate <= todayDate && job.status !== 'Klaar';
                                    return (
                                    <tr key={job.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                        <td className="px-4 py-4">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedJobIds.has(job.id)} 
                                                onChange={() => toggleJobSelection(job.id)}
                                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-xs text-slate-400">{formatDateEU(job.date)}</div>
                                            <div 
                                                className="font-medium text-slate-900 dark:text-white cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                                onClick={() => { const c = customers.find(cust => cust.id === job.customerId); if (c) openCustomerHistory(c); }}
                                            >
                                                {getCustomerName(job.customerId)}
                                            </div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">{getRacketName(job)}</div>
                                            <div className="mt-1.5">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800/50">
                                                    <User className="w-3 h-3 mr-1" />
                                                    {job.stringer ? job.stringer.split(' ')[0] : 'Niemand'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4"><div>{job.string}</div>{job.baseGrip && <div className="text-xs text-slate-500 dark:text-slate-400">+ BG: {job.baseGrip}</div>}{job.overGrip && <div className="text-xs text-slate-500 dark:text-slate-400">+ OG: {job.overGrip}</div>}<div className="text-xs">{job.tensionMain}/{job.tensionCross} {job.tensionUnit || 'kg'}</div></td>
                                        <td className="px-6 py-4">
                                            {job.deadline ? (
                                                <div className={`flex items-center text-xs font-medium ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                                    {isUrgent && <AlertTriangle className="w-3 h-3 mr-1" />}
                                                    {formatDateEU(job.deadline)} {job.deadlineTime && <span className="ml-1 font-bold">{job.deadlineTime}</span>}
                                                </div>
                                            ) : <span className="text-xs text-slate-300 dark:text-slate-600">-</span>}
                                        </td>
                                        <td className="px-6 py-4"><button onClick={() => cycleStatus(job)} className={`px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1 ${job.status === 'Klaar' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>{job.status}</button></td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <span className="font-medium mr-2 text-slate-900 dark:text-white">€ {formatPrice(job.price)}</span>
                                                <button onClick={() => togglePaid(job)} className={`p-2 rounded-full ${job.paid ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-300 dark:bg-slate-700 dark:text-slate-400'}`} title={job.paid ? "Betaald" : "Markeer als betaald"}><Euro className="w-4 h-4" /></button>
                                                
                                                <button 
                                                    onClick={() => job.paymentLink ? setPaymentModalJob(job) : generateMollieLink(job)} 
                                                    className={`p-2 rounded-full ${job.paymentLink ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-blue-100 hover:text-blue-600'}`} 
                                                    title={job.paymentLink ? "Bekijk Betaalverzoek" : "Genereer Mollie Betaallink"}
                                                >
                                                    {generatingMollieId === job.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (job.paymentLink ? <Link className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />)}
                                                </button>
                                                
                                                <button onClick={() => openPrintModal(job)} className="p-2 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-indigo-100 hover:text-indigo-600" title="Print Kaart"><Printer className="w-4 h-4" /></button>

                                                <button onClick={() => duplicateJob(job)} className="p-2 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-indigo-100 hover:text-indigo-600" title="Nog een keer"><Repeat className="w-4 h-4" /></button>
                                                <button onClick={() => openJobEditModal(job)} className="p-2 rounded-full text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/50" title="Bewerken"><Pencil className="w-4 h-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* COMPACTE MOBIELE WEERGAVE */}
                        <div className="md:hidden flex flex-col gap-2 p-2 bg-slate-50/50 dark:bg-slate-900/30">
                            {filteredJobs.map(job => {
                                const deadlineDate = job.deadline ? new Date(`${job.deadline}T${job.deadlineTime || '23:59'}`) : null;
                                const todayDate = new Date();
                                const isUrgent = deadlineDate && deadlineDate <= todayDate && job.status !== 'Klaar';
                                const displayDate = job.deadline ? `${formatDateEU(job.deadline)}${job.deadlineTime ? ` ${job.deadlineTime}` : ''}` : formatDateEU(job.date);

                                return (
                                    <div key={`mobile-${job.id}`} className="bg-white dark:bg-slate-800 p-3 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col relative">
                                        <div className="flex justify-between items-start mb-1.5">
                                            <div className="flex items-center gap-2 overflow-hidden pr-2">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedJobIds.has(job.id)} 
                                                    onChange={() => toggleJobSelection(job.id)}
                                                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700 shrink-0"
                                                />
                                                <span 
                                                    className="font-bold text-slate-900 dark:text-white text-[13px] leading-tight truncate cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                                    onClick={() => { const c = customers.find(cust => cust.id === job.customerId); if (c) openCustomerHistory(c); }}
                                                >
                                                    {getCustomerName(job.customerId)}
                                                </span>
                                                {isUrgent && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                                            </div>
                                            <div className="flex flex-col items-end shrink-0">
                                                <button onClick={() => cycleStatus(job)} className={`px-2 py-0.5 rounded-md text-[9px] uppercase tracking-wider font-bold transition flex items-center shrink-0 ${job.status === 'Klaar' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                                                    {job.status}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center mb-1 pl-6">
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate pr-2">{getRacketName(job)}</span>
                                            <span className="font-bold text-slate-900 dark:text-white text-sm shrink-0">€ {formatPrice(job.price)}</span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight mb-2 truncate pl-6">
                                            {job.string} {job.isHybrid && job.crossString ? `/ ${job.crossString}` : ''}
                                            <span className="mx-1">•</span>
                                            {job.tensionMain}/{job.tensionCross} {job.tensionUnit || 'kg'}
                                            {job.stringer && <><span className="mx-1">•</span><User className="w-2.5 h-2.5 inline mr-0.5 mb-0.5"/>{job.stringer.split(' ')[0]}</>}
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-auto">
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <button onClick={() => togglePaid(job)} className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider transition shrink-0 flex items-center ${job.paid ? 'text-purple-700 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300' : 'text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                                                    {job.paid ? 'Betaald' : 'Onbetaald'}
                                                </button>
                                                <span className="text-[10px] font-bold text-slate-400 truncate">{displayDate}</span>
                                            </div>
                                            <div className="flex gap-1.5 shrink-0 ml-2">
                                                <button onClick={() => job.paymentLink ? setPaymentModalJob(job) : generateMollieLink(job)} className={`w-7 h-7 flex items-center justify-center rounded-lg ${job.paymentLink ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-blue-100 hover:text-blue-600'}`}>
                                                    {generatingMollieId === job.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (job.paymentLink ? <Link className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />)}
                                                </button>
                                                
                                                <button onClick={() => openPrintModal(job)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-indigo-100 hover:text-indigo-600" title="Print Kaart"><Printer className="w-3.5 h-3.5" /></button>

                                                <button onClick={() => duplicateJob(job)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-indigo-100 hover:text-indigo-600" title="Nog een keer"><Repeat className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => openJobEditModal(job)} className="w-7 h-7 flex items-center justify-center rounded-lg text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/70"><Pencil className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        </>
                    ) : <div className="p-8 text-center text-slate-500 dark:text-slate-400">Geen opdrachten.</div>}
                </div>
            </div>
        </div>
    );
}